import React from 'react';
import styled from '@emotion/styled';
import { Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
// eslint-disable-next-line import/no-duplicates
import { ru } from 'date-fns/locale';
// eslint-disable-next-line import/no-duplicates
import { addSeconds } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { upperFirst } from 'lodash-es';
import { VisitResponse } from 'types';
import { CancelCircleIcon, CheckCircleIcon } from 'static/icons';
import { LOCAL_TIMEZONE } from 'constants/dates';
import { PrimaryButton } from 'components';
import { CancellationDialog } from './components/CancellationDialog';
import { CancellationExceedDialog } from './components/CancellationExceedDialog';

const ContainerStyled = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
`;

const CancelCircleIconStyled = styled(CancelCircleIcon)`
  color: ${({ theme }) => theme.colors.highlight};
  margin-top: auto;
`;

const CheckCircleIconStyled = styled(CheckCircleIcon)`
  color: ${({ theme }) => theme.colors.highlight};
  margin-top: auto;
`;

export const VisitInfoPage: React.FC<{
  homeLink: string;
  response: VisitResponse;
  companyName: string;
  address: string;
  isRejectAllowed: () => boolean;
  cancelVisit: () => void;
  statusInfo: React.ReactNode;
}> = ({
  homeLink,
  response,
  companyName,
  address,
  isRejectAllowed,
  cancelVisit,
  statusInfo,
}) => {
  const {
    isOpen: cancellationDialogOpen,
    onClose: onCancellationDialogClose,
    onOpen: onCancellationDialogOpen,
  } = useDisclosure();
  const {
    isOpen: exceedDialogOpen,
    onClose: onExceedDialogClose,
    onOpen: onExceedDialogOpen,
  } = useDisclosure();
  const {
    company,
    duration,
    date,
    places,
    finalSum,
    state,
    stateClient,
    timezone = LOCAL_TIMEZONE,
  } = response;
  const isRejected = [state, stateClient].includes('reject');

  const startDate = new Date(date);
  const startMessage = formatInTimeZone(
    startDate,
    timezone,
    'на d MMMM yyyy с HH:mm',
    {
      locale: ru,
    },
  );

  const onCancelVisit = React.useCallback(() => {
    onCancellationDialogClose();
    if (isRejectAllowed()) {
      cancelVisit();
    } else {
      onExceedDialogOpen();
    }
  }, [
    onCancellationDialogClose,
    isRejectAllowed,
    cancelVisit,
    onExceedDialogOpen,
  ]);

  return (
    <ContainerStyled>
      {statusInfo}
      {isRejected ? (
        <>
          <CancelCircleIconStyled width={106} height={106} />
          <Text color="black" textStyle="h2" mt="6">
            Ваша запись отменена
          </Text>
        </>
      ) : (
        <>
          <CheckCircleIconStyled width={106} height={106} />
          <Text color="black" textStyle="h2" mt="6">
            Вы успешно записаны
          </Text>
          <Text
            color="grayText"
            textStyle="body1"
            m="4"
            maxW="340px"
            align="center"
          >
            В&nbsp;
            <span translate="no">{company?.name ?? companyName}</span>
            <br />
            {address && (
              <>
                по адресу:&nbsp;
                <span translate="no">{address}</span>
                <br />
              </>
            )}
            <span>{startMessage}</span>
            {duration ? (
              <span>
                {formatInTimeZone(
                  addSeconds(startDate, duration),
                  timezone,
                  ' до HH:mm',
                  {
                    locale: ru,
                  },
                )}
              </span>
            ) : null}
            <span>
              {` (${upperFirst(
                startDate.toLocaleString('ru', {
                  timeZone: timezone || undefined,
                  weekday: 'long',
                }),
              )})`}
            </span>
            <br />
            {places ? (
              <>
                <span>Количество мест: {places}</span>
                <br />
              </>
            ) : null}
            {finalSum ? (
              <>
                <span>{`Итого: ${finalSum} ₽`}</span>
                <br />
              </>
            ) : null}
          </Text>
        </>
      )}
      <Flex flexDirection="column" mt="auto" mb={{ base: '74px', md: '143px' }}>
        <PrimaryButton as="a" href={homeLink} w="199px">
          Записаться еще
        </PrimaryButton>
        {!isRejected && isRejectAllowed() && (
          <Button
            variant="outline"
            borderRadius="12px"
            border="none"
            w="199px"
            p="24px 16px"
            mt={2}
            size="lg"
            fontWeight="400"
            color="highlight"
            onClick={onCancellationDialogOpen}
          >
            Отменить запись
          </Button>
        )}
      </Flex>
      <CancellationDialog
        isOpen={cancellationDialogOpen}
        onClose={onCancellationDialogClose}
        onVisitCancel={onCancelVisit}
      />
      <CancellationExceedDialog
        isOpen={exceedDialogOpen}
        onClose={onExceedDialogClose}
      />
    </ContainerStyled>
  );
};
